import { normalize } from './normalize';

// The Monthly table's expired-car marks (the plate's red 'Exp', "registration exp 18/09/2026", the row stripe).
// Pure: the page's hook (contracts.logic) builds the index once per fleet list with useMemo.
//
// Speed (owner, 28 Sep 2026: the Monthly page took ~4 s to show): every plate cell used to scan the whole fleet
// (~1,600 cars), normalising both plates of every car again - about six scans a row, ~1,000 scans for 171 rows,
// ~1.7 s of the page's first render. The rules below are the hook's, unchanged.

/**
 * The fleet by plate (utils/normalize): a plate finds the FIRST car, in the fleet's order, whose plate_number or
 * plate_no reads the same - the car the old scan found.
 */
export const indexCarsByPlate = (carsData) => {
    const byPlate = new Map();
    (carsData || []).forEach((c) => {
        if (!c) return;
        [normalize(c.plate_number), normalize(c.plate_no)].forEach((key) => { if (!byPlate.has(key)) byPlate.set(key, c); });
    });
    return byPlate;
};

const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
    }
    return new Date(dateStr);
};

/** The car's registration - or its insurance - ended before `today`. */
export const isCarExpiredOn = (car, today = new Date()) => {
    if (car.registration_expiry) {
        const regDate = parseDate(car.registration_expiry);
        if (regDate && regDate < today) return true;
        // Company rule: insurance ends one month AFTER the registration - derived, so a
        // stale stored insurance date never flags a freshly registered car.
        if (regDate) {
            const ins = new Date(regDate); ins.setMonth(ins.getMonth() + 1);
            return ins < today;
        }
    }
    if (car.insurance_expiry) {
        const insDate = parseDate(car.insurance_expiry);
        if (insDate && insDate < today) return true;
    }
    return false;
};

/**
 * WHAT expired on the car (registration / insurance) and when - the table shows it next to the plate, so "Exp" is
 * never a mystery ("insurance 18/09/2026"). null when nothing expired.
 */
export const expiryDetailOf = (car, today = new Date()) => {
    const parse = (s) => { const p = String(s || '').split('/'); return p.length === 3 ? new Date(+p[2], +p[1] - 1, +p[0]) : (s ? new Date(s) : null); };
    // insurance = registration + 1 month (company rule); the stored date only when no registration
    const reg = parse(car.registration_expiry);
    let insRaw = car.insurance_expiry;
    if (reg && !isNaN(reg)) {
        const d = new Date(reg); d.setMonth(d.getMonth() + 1);
        insRaw = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    }
    const expired = [
        ['registration', car.registration_expiry],
        ['insurance', insRaw],
    ].map(([kind, raw]) => ({ kind, raw, date: parse(raw) })).filter(x => x.date && !isNaN(x.date) && x.date < today);
    if (!expired.length) return null;
    expired.sort((a, b) => a.date - b.date);
    return { kind: expired.map(x => x.kind).join(' + '), date: expired[0].raw, all: expired };
};

/**
 * The table's two marks for one fleet list (the hook keeps it with useMemo on the list):
 *   isCarExpired(plate)   - the plate's car has an expired registration / insurance
 *   getExpiryDate(plate)  - what expired and when (expiryDetailOf), or null
 * A plate with no car in the fleet is never expired.
 */
export const fleetExpiry = (carsData) => {
    const byPlate = indexCarsByPlate(carsData);
    const hasCars = (carsData || []).length > 0;
    const carOf = (plateNo) => (plateNo && hasCars ? byPlate.get(normalize(plateNo)) : undefined);
    return {
        isCarExpired: (plateNo) => {
            const car = carOf(plateNo);
            return car ? isCarExpiredOn(car) : false;
        },
        getExpiryDate: (plateNo) => {
            const car = carOf(plateNo);
            return car ? expiryDetailOf(car) : null;
        },
    };
};
